import type { onChangeEvent } from "@waldner-laboreinrichtungen/component-system";
import {
  Button,
  ButtonGroup,
  Chart,
  FlexBox,
  ModalDropdown,
  space,
  theme,
} from "@waldner-laboreinrichtungen/component-system";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import styledComponents, { css } from "styled-components";

import { useDataPointsForChannel } from "../hooks/use-datapoints";
import type { Channel, Unit } from "../view-model";

const StyledDetailEvaluation = styledComponents.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${space(6)};
`;

type TimeOption = {
  id: number;
  label: string;
  value: Date;
  groupTimeRange?: string;
  sampleSize?: number;
  variant?: string;
};

const StyledCircle = styledComponents.span<{ circlecolor: string }>`
  ${({ circlecolor }) =>
    circlecolor &&
    css`
      width: ${space(2)};
      height: ${space(2)};
      content: "";
      border-radius: ${theme.borderRadius.inf};
      background-color: ${circlecolor};
    `}
`;

const getTicks = (timeOption: TimeOption) => {
  const now = new Date(Date.now());
  const ticks = [];
  if (timeOption.id === 0) {
    now.setMilliseconds(0);
    now.setSeconds(0);
    now.setMinutes(Math.floor(now.getMinutes() / 10) * 10);
    const lastTick = now.valueOf();
    for (let index = 0; index <= 50; index = index + 10) {
      ticks.push(lastTick - 1000 * 60 * index);
    }
  }
  if (timeOption.id === 1) {
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
    const lastTick = now.valueOf();
    for (let index = 1; index <= 24; index = index + 1) {
      ticks.push(lastTick - 1000 * 60 * 60 * index);
    }
  }
  if (timeOption.id === 2) {
    now.setHours(0);
    now.setMinutes(0);
    const lastTick = now.valueOf();
    for (let index = 0; index <= 6; index = index + 1) {
      ticks.push(lastTick - 1000 * 60 * 60 * 24 * index);
    }
  }
  return ticks.reverse();
};

export type DetailEvaluationProps = {
  units: Unit[];
};

type ChannelOptions = {
  label: string;
  value: Channel;
  /** Channel ID for TargetValue history (if available) */
  targetChannelId?: string;
};

export const DetailEvaluation: FC<DetailEvaluationProps> = ({ units }) => {
  const { i18n, t } = useTranslation();

  // Build channel options from units.functions
  // Only show channels with historizing === true (OPC UA standard attribute)
  // Only use CurrentValue (TargetValue is metadata, not a separate timeline)
  const unitOptions = units
    .map((unit) => {
      const options: ChannelOptions[] = [];

      for (const [, channel] of Object.entries(unit.functions || {})) {
        if (channel.isEnabled === false) continue;
        if (channel.historizing !== true) continue;

        const historizedVars = channel.historizedVariables;

        // Use first historized variable (backend determines priority order)
        const varName = historizedVars?.[0];

        // Check if TargetValue is also historized
        const hasTarget = historizedVars?.includes("TargetValue");
        const targetChannelId = hasTarget
          ? `${channel.id}/targetValue`
          : undefined;

        if (varName) {
          const varChannelId = `${channel.id}/${varName
            .charAt(0)
            .toLowerCase()}${varName.slice(1)}`;
          options.push({
            label: channel.name || channel.id,
            value: {
              ...channel,
              id: varChannelId,
            },
            targetChannelId,
          });
        } else {
          // No historizedVariables array - use channel as-is (fallback)
          options.push({
            label: channel.name || channel.id,
            value: channel,
            targetChannelId,
          });
        }
      }

      return {
        title: unit.name || unit.id,
        optionValues: options,
      };
    })
    .filter((unit) => unit.optionValues.length > 0);

  // Flatten all options for initial selection
  const allOptions = unitOptions.flatMap((u) => u.optionValues);

  // Build modal options with string values for ModalDropdown (radio buttons need unique string values)
  // Prefix channel IDs with unit title to ensure uniqueness when the same Function appears
  // under multiple FunctionalUnits (e.g. via OPC UA Organizes references)
  const channelMap = new Map<string, ChannelOptions>();
  const reverseChannelMap = new Map<string, string>();
  const modalOptions = unitOptions.map((group) => ({
    title: group.title,
    optionValues: group.optionValues.map((o) => {
      const uniqueKey = `${group.title}/${o.value.id}`;
      channelMap.set(uniqueKey, o);
      reverseChannelMap.set(o.value.id, uniqueKey);
      return { label: o.label, value: uniqueKey, key: uniqueKey };
    }),
  }));

  const timeOptions: TimeOption[] = [
    {
      id: 0,
      label: t("time.60m"),
      value: new Date(Date.now() - 1000 * 60 * 60),
      sampleSize: 100,
      variant: "hour",
    },
    {
      id: 1,
      label: t("time.24h"),
      value: new Date(Date.now() - 1000 * 60 * 60 * 24),
      sampleSize: 100,
      variant: "day",
    },
    {
      id: 2,
      label: t("time.7d"),
      value: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7),
      sampleSize: 100,
      variant: "week",
    },
  ];

  const [selectedChannelOne, setSelectedChannelOne] = useState<ChannelOptions>(
    allOptions[0]
  );
  // Default to second option, or first if only one available
  const [selectedChannelTwo, setSelectedChannelTwo] = useState<ChannelOptions>(
    allOptions[1] || allOptions[0]
  );
  const [selectedTimeRange, setSelectedTimeRange] = useState(timeOptions[0]);

  const [currentOptionLeft, setCurrentOptionLeft] =
    useState(selectedChannelOne);
  const [currentOptionRight, setCurrentOptionRight] =
    useState(selectedChannelTwo);

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const foundOptionOne = allOptions.find(
      (option) => option.value.id === selectedChannelOne?.value.id
    );
    if (foundOptionOne) {
      setSelectedChannelOne(foundOptionOne);
    } else if (allOptions.length > 0 && !selectedChannelOne) {
      setSelectedChannelOne(allOptions[0]);
    }
    const foundOptionTwo = allOptions.find(
      (option) => option.value.id === selectedChannelTwo?.value.id
    );
    if (foundOptionTwo) {
      setSelectedChannelTwo(foundOptionTwo);
    } else if (allOptions.length > 1 && !selectedChannelTwo) {
      setSelectedChannelTwo(allOptions[1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, t]);

  const formatTick = (tick: number): string => {
    if (selectedTimeRange.id === 0) {
      return new Date(tick).toLocaleTimeString();
    }

    if (selectedTimeRange.id === 1) {
      return `${new Date(tick).getHours()}:00`;
    }

    if (selectedTimeRange.id === 2) {
      return new Intl.DateTimeFormat("de-DE", { weekday: "long" }).format(
        new Date(tick)
      );
    }
    return `${tick}`;
  };

  const {
    channelDataPoints: channelDataPointsOne = [],
    enumStrings: enumStringsOne,
    isLoading: isLoadingOne,
  } = useDataPointsForChannel(
    selectedTimeRange.value,
    selectedChannelOne?.value.id,
    selectedTimeRange.groupTimeRange,
    selectedTimeRange.sampleSize,
    selectedTimeRange.variant,
    selectedChannelOne?.value.name === "operation_status",
    selectedChannelOne?.value.browsePath
  );

  // Fetch TargetValue history for left channel (if available)
  const { channelDataPoints: targetDataPointsOne = [] } =
    useDataPointsForChannel(
      selectedTimeRange.value,
      selectedChannelOne?.targetChannelId,
      selectedTimeRange.groupTimeRange,
      selectedTimeRange.sampleSize,
      `${selectedTimeRange.variant}-target`,
      false,
      selectedChannelOne?.value.browsePath
    );

  const {
    channelDataPoints: channelDataPointsTwo = [],
    enumStrings: enumStringsTwo,
    isLoading: isLoadingTwo,
  } = useDataPointsForChannel(
    selectedTimeRange.value,
    selectedChannelTwo?.value.id,
    selectedTimeRange.groupTimeRange,
    selectedTimeRange.sampleSize,
    selectedTimeRange.variant,
    selectedChannelTwo?.value.name === "operation_status",
    selectedChannelTwo?.value.browsePath
  );

  // Fetch TargetValue history for right channel (if available)
  const { channelDataPoints: targetDataPointsTwo = [] } =
    useDataPointsForChannel(
      selectedTimeRange.value,
      selectedChannelTwo?.targetChannelId,
      selectedTimeRange.groupTimeRange,
      selectedTimeRange.sampleSize,
      `${selectedTimeRange.variant}-target`,
      false,
      selectedChannelTwo?.value.browsePath
    );

  // Resolve enumStrings: prefer History API response, fall back to channel model
  const resolvedEnumStringsOne =
    enumStringsOne || selectedChannelOne?.value.enumStrings;
  const resolvedEnumStringsTwo =
    enumStringsTwo || selectedChannelTwo?.value.enumStrings;

  // Create tick formatter from enumStrings (if available)
  const createEnumTickFormatter = (enumStrings?: string[]) => {
    if (!enumStrings || enumStrings.length === 0) return undefined;
    return (value: number) => {
      if (Number.isInteger(value) && value >= 0 && value < enumStrings.length) {
        return enumStrings[value];
      }
      return String(value);
    };
  };

  // Build the explicit Y-tick set for an enum axis. Per policy, all
  // declared enum states are shown on the Y axis (not just those that
  // appear in the visible window), so the operator always knows the
  // full state space. State numbers can be sparse — e.g. Cover uses
  // 1=Closed, 2=Error, 3=Locked, 4=Opened — so we emit every index
  // whose enumStrings entry is non-empty.
  const buildEnumTicks = (
    enumStrings: string[] | undefined
  ): number[] | undefined => {
    if (!enumStrings?.length) return undefined;
    const ticks = enumStrings
      .map((label, i) => (label ? i : -1))
      .filter((i) => i >= 0);
    return ticks.length > 0 ? ticks : undefined;
  };

  const updateSelectedOptions = (event: onChangeEvent) => {
    const channelId = event.option.value as string;
    const option = channelMap.get(channelId);
    if (!option) return;
    if (event.position === "left") {
      setCurrentOptionLeft(option);
    } else {
      setCurrentOptionRight(option);
    }
  };

  const onAccept = () => {
    setSelectedChannelOne(currentOptionLeft);
    setSelectedChannelTwo(currentOptionRight);
    setIsModalOpen(false);
  };

  // No channels available
  if (allOptions.length === 0) {
    return (
      <StyledDetailEvaluation>
        <FlexBox alignItems="center" flexGrow={1} justifyContent="center">
          {t("detail.evaluation.noChannels") ||
            "No channels available for evaluation"}
        </FlexBox>
      </StyledDetailEvaluation>
    );
  }

  // Only one channel available - show single chart
  if (allOptions.length === 1 || !selectedChannelTwo) {
    return (
      <StyledDetailEvaluation>
        <FlexBox justifyContent="space-between">
          {isLoadingOne ? (
            <Skeleton width="215px" />
          ) : (
            <Button onClick={() => setIsModalOpen(true)} variant="legend">
              <StyledCircle circlecolor={theme.color.info} />
              {selectedChannelOne?.label || "Select channel"}
            </Button>
          )}
          <ButtonGroup isCompact>
            {timeOptions.map((timeOption) => (
              <Button
                key={timeOption.id}
                onClick={() => setSelectedTimeRange(timeOption)}
                variant={
                  selectedTimeRange.id === timeOption.id
                    ? "primary"
                    : "secondary"
                }
              >
                {timeOption.label}
              </Button>
            ))}
          </ButtonGroup>
        </FlexBox>
        <FlexBox flexGrow={1} marginTop={space(8)}>
          <Chart
            xAxisEndPoint={Date.now()}
            xAxisStartPoint={selectedTimeRange.value.valueOf()}
            xAxisTickFormatter={formatTick}
            xAxisTicks={getTicks(selectedTimeRange)}
            yAxisOneData={channelDataPointsOne}
            yAxisOneTargetData={
              targetDataPointsOne.length > 0 ? targetDataPointsOne : undefined
            }
            yAxisOneTickFormatter={createEnumTickFormatter(
              resolvedEnumStringsOne
            )}
            yAxisOneTicks={buildEnumTicks(resolvedEnumStringsOne)}
            yAxisOneUnit={
              resolvedEnumStringsOne
                ? ""
                : " " + (selectedChannelOne?.value.unit || "")
            }
            yAxisTwoData={[]}
            yAxisTwoUnit=""
          />
        </FlexBox>
      </StyledDetailEvaluation>
    );
  }

  return (
    <StyledDetailEvaluation>
      <FlexBox justifyContent="space-between">
        {isLoadingOne ? (
          <Skeleton width="215px" />
        ) : (
          <Button onClick={() => setIsModalOpen(true)} variant="legend">
            <StyledCircle circlecolor={theme.color.info} />
            {selectedChannelOne?.label || "Select channel"}
          </Button>
        )}
        <ButtonGroup isCompact>
          {timeOptions.map((timeOption) => (
            <Button
              key={timeOption.id}
              onClick={() => setSelectedTimeRange(timeOption)}
              variant={
                selectedTimeRange.id === timeOption.id ? "primary" : "secondary"
              }
            >
              {timeOption.label}
            </Button>
          ))}
        </ButtonGroup>
        {isLoadingTwo ? (
          <Skeleton width="215px" />
        ) : (
          <Button onClick={() => setIsModalOpen(true)} variant="legend">
            <StyledCircle circlecolor={theme.color.warning} />
            {selectedChannelTwo?.label || "Select channel"}
          </Button>
        )}
      </FlexBox>
      <FlexBox flexGrow={1} marginTop={space(8)}>
        <Chart
          xAxisEndPoint={Date.now()}
          xAxisStartPoint={selectedTimeRange.value.valueOf()}
          xAxisTickFormatter={formatTick}
          xAxisTicks={getTicks(selectedTimeRange)}
          yAxisOneData={channelDataPointsOne}
          yAxisOneTargetData={
            targetDataPointsOne.length > 0 ? targetDataPointsOne : undefined
          }
          yAxisOneTickFormatter={createEnumTickFormatter(
            resolvedEnumStringsOne
          )}
          yAxisOneTicks={buildEnumTicks(resolvedEnumStringsOne)}
          yAxisOneUnit={
            resolvedEnumStringsOne
              ? ""
              : " " + (selectedChannelOne?.value.unit || "")
          }
          yAxisTwoData={channelDataPointsTwo}
          yAxisTwoTargetData={
            targetDataPointsTwo.length > 0 ? targetDataPointsTwo : undefined
          }
          yAxisTwoTickFormatter={createEnumTickFormatter(
            resolvedEnumStringsTwo
          )}
          yAxisTwoTicks={buildEnumTicks(resolvedEnumStringsTwo)}
          yAxisTwoUnit={
            resolvedEnumStringsTwo
              ? ""
              : " " + (selectedChannelTwo?.value.unit || "")
          }
        />
      </FlexBox>
      <ModalDropdown
        acceptText={t("diagram.modal.compare")}
        closeText={t("diagram.modal.close")}
        onAccept={() => onAccept()}
        onChange={(e) => updateSelectedOptions(e)}
        onClose={() => setIsModalOpen(false)}
        open={isModalOpen}
        options={modalOptions}
        selectedOptionLeft={
          selectedChannelOne
            ? {
                label: selectedChannelOne.label,
                value:
                  reverseChannelMap.get(selectedChannelOne.value.id) ??
                  selectedChannelOne.value.id,
              }
            : undefined
        }
        selectedOptionRight={
          selectedChannelTwo
            ? {
                label: selectedChannelTwo.label,
                value:
                  reverseChannelMap.get(selectedChannelTwo.value.id) ??
                  selectedChannelTwo.value.id,
              }
            : undefined
        }
        title={t("diagram.modal.title")}
      />
    </StyledDetailEvaluation>
  );
};
